import { Box, Divider, Grid2 } from "@mui/material";
import { format, isSameDay, startOfDay } from "date-fns";
import addDays from "date-fns/addDays";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { WORKERS } from "../../components/full-time-scheduler/constants";
import FTCalendarToolbar from "../../design/ft-calendar-toolbar";
import { useDateFnsLocale } from "../../utils/date-locale";
import useViewport from "../../utils/use-viewport";
import AppliedFilters from "../filters/applied-filters";
import { FilterContext } from "../filters/filter-context";
import useWorkerSchedulerController from "./controllers/use-worker-scheduler-controller";
import useWorkerSchedulerData from "./controllers/use-worker-scheduler-data";
import { fetchFTWorkerShifts, fetchWorkerFTShiftDetails, resetFetchFTWorkerShiftDetailsStatus } from "./store/worker-scheduler-slice";
import { CalendarHeader } from "./ui/calendar-header";
import GlobalTransitionDialogManager from "./ui/global-transition-dialog-manager";
import WorkcenterShiftpatternView from "./ui/workcenter-shiftpattern-view.tsx";
import WorkerSchedulerSkeleton from "./ui/worker-scheduler-skeleton";

interface WorkerShift {
  workerId: string;
  badgeNumber: number | null;
  primaryJobName: string;
  workerName: string;
  firstName: string;
  lastName: string;
  shifts: any[];
  dPrimaryJobId: number;
  companyEmployeeId: string;
  dwCenterId: number;
  primaryJobId: number;
  patternId: number;
  uPrimaryJobId: number;
  workerTypeId: string;
  workerSubTypeId: string;
  isWorkcenterChanged?: boolean;
  isJobChanged?: boolean;
  isPatternChanged?: boolean;
  workCenterId?: number;
}

const WorkerScheduler: React.FC = () => {
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
  const filterContext = useContext(FilterContext);
  const setIsLoading = filterContext?.setIsLoading;
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
	const [
		{
      appliedFilters,
			fetchFTWorkerFiltersStatus,
			startingDay,
			isShiftPatternOpen,
			isWorkcenterOpen,
			shiftPatternsObj,
			viewMode,
			fetchFTWorkerShiftsStatus,
			filters,
			isFilterOpen,
			defaultAppliedFilters,
			workerWorkcenterShiftPatternsMap,
			selectedLocation,
		},
		{
			openShiftPattern,
			openWorkcenter,
			closeShiftPattern,
			closeWorkcenter,
			setFilters,
			setIsFilterOpen,
		},
	] = useWorkerSchedulerController({
    isPrinting: isPrinting || isExporting,
  });

  useEffect(() => {
    if (setIsLoading) {
      if (fetchFTWorkerFiltersStatus === "pending") {
        setIsLoading(true);
      } else if (
        fetchFTWorkerFiltersStatus === "fulfilled" ||
        fetchFTWorkerFiltersStatus === "rejected"
      ) {
        setIsLoading(false);
      }
    }
  }, [fetchFTWorkerFiltersStatus, setIsLoading]);

  useEffect(() => {

    const handleBeforePrint = () => {
      setIsPrinting(true);
    };

    const handleAfterPrint = () => {
      setIsPrinting(false);
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const { filterData } = useWorkerSchedulerData();
  const { allJobsObj, schedulingStatusesObj, allWorkcentersObj } = filterData;

  const { searchedTerm } = useWorkerSchedulerData();

	const { height } = useViewport();
	const boxRef: any = useRef();
	const [schedulerContainerHeight, setSchedulerContainerHeight] = useState(0);
	const dispatch = useDispatch();

  const allLocations = useSelector(
    (state: any) => state.fullTimeShifts?.filterData?.allLocations
  );

  const fetchFTWorkerShiftDetailsData = useSelector(
    (state: any) => state.workerScheduler?.fetchFTWorkerShiftDetails?.data
  );
  const fetchFTWorkerShiftDetailsStatus = useSelector(
    (state: any) => state.workerScheduler?.fetchFTWorkerShiftDetails?.status
  );
  const selectedLocationName = useMemo(() => {
    if (selectedLocation === null) return "location";
    const location = allLocations.find(
      (loc: { id: number }) => loc.id === selectedLocation
    );
    // Sanitize name for file
    return location ? location.name.replace(/[^a-zA-Z0-9]/g, "_") : "location";
  }, [selectedLocation, allLocations]);

  const handleExport = useCallback(() => {
    const payload: { workcenterId: number; shiftId: string }[] = [];
    const seen = new Set<string>();

    const startOfWeekDate = startingDay;
    const endOfWeekDate = addDays(startOfWeekDate, 7);

    workerWorkcenterShiftPatternsMap.forEach((wc) => {
      wc.shiftPatterns.forEach((sp: any) => {
        sp.workers.forEach((worker: any) => {
          worker.shifts?.forEach((shift: any) => {
            if (!shift.isFlexShift && shift.shiftId) {
              const shiftDate = startOfDay(new Date(shift.startDateTime));
              if (shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate) {
                const key = `${wc.id}_${shift.shiftId}`;
                if (!seen.has(key)) {
                  payload.push({
                    workcenterId: wc.id,
                    shiftId: shift.shiftId,
                  });
                  seen.add(key);
                }
              }
            }
          });
        });
      });
    });

    if (payload.length > 0) {
      setIsFetchingDetails(true);
      dispatch(resetFetchFTWorkerShiftDetailsStatus());
      dispatch(fetchWorkerFTShiftDetails(payload) as any);
    }
    setIsExporting(true);
  }, [workerWorkcenterShiftPatternsMap, dispatch, startingDay]);

  useEffect(() => {
    // Only run if isExporting is true AND the data is not loading
    if (
      isExporting &&
      fetchFTWorkerFiltersStatus === "fulfilled" &&
      fetchFTWorkerShiftsStatus === "fulfilled"
    ) {
      const exportToExcel = async () => {
        try {
          const workbook = new ExcelJS.Workbook();
          const summarySheet = workbook.addWorksheet(
            t("full-time-worker-scheduler_schedule-summary")
          );

          // --- 1. Define Styles ---
          const border: Partial<ExcelJS.Borders> = {
            top: { style: "thin", color: { argb: "FFE5E5E5" } },
            left: { style: "thin", color: { argb: "FFE5E5E5" } },
            bottom: { style: "thin", color: { argb: "FFE5E5E5" } },
            right: { style: "thin", color: { argb: "FFE5E5E5" } },
          };

          const wcHeaderStyle: Partial<ExcelJS.Style> = {
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3E6ED" } },
            font: { name: "Roboto", size: 14, bold: true },
            alignment: { vertical: "middle", horizontal: "left" },
            border,
          };

          const jobHeaderStyle: Partial<ExcelJS.Style> = {
            font: { name: "Roboto", size: 11, bold: true, underline: true },
            alignment: { vertical: "middle", horizontal: "left", indent: 2 },
          };

          const mainHeaderStyle: Partial<ExcelJS.Style> = {
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFAFAFA" } },
            font: { name: "Roboto", size: 12, bold: true, color: { argb: "FF000000" } },
            alignment: { vertical: "middle", horizontal: "left" },
            border,
          };

          const dateHeaderStyle: Partial<ExcelJS.Style> = {
            ...mainHeaderStyle,
            alignment: { vertical: "middle", horizontal: "center" },
          };

          const workerCellStyle: Partial<ExcelJS.Style> = {
            font: { name: "Roboto", size: 10 },
            alignment: { vertical: "middle", horizontal: "left", wrapText: true },
            border,
          };

          const openWorkerCellStyle: Partial<ExcelJS.Style> = {
            ...workerCellStyle,
            font: { ...workerCellStyle.font, italic: true },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFE0" } }, // Light Yellow
          };

          const legendCellStyle: Partial<ExcelJS.Style> = {
            font: { name: "Roboto", size: 10 },
            alignment: { vertical: "middle", horizontal: "left" },
          };

          const workerStatusCellStyle: Partial<ExcelJS.Style> = {
            font: { name: "Roboto", size: 10, bold: true },
            alignment: { vertical: "middle", horizontal: "center", wrapText: true },
            border,
          };

          const shiftSummaryNameStyle: Partial<ExcelJS.Style> = {
            ...workerCellStyle,
            font: { ...workerCellStyle.font, bold: true },
            alignment: { vertical: "middle", horizontal: "left", indent: 2 },
            border,
          };

          const shiftSummaryTimeStyle: Partial<ExcelJS.Style> = {
            ...workerCellStyle,
            alignment: { vertical: "middle", horizontal: "center", wrapText: true },
            border,
          };

          // --- 2. Define Columns & Date Headers ---
          const startOfWeekDate = startingDay;
          const endOfWeekDate = addDays(startOfWeekDate, 7);
          const dateColumns = Array.from({ length: 7 }, (_, i) => addDays(startOfWeekDate, i));

          // --- 3. Helper Functions ---
          const statusIdToObjMap = new Map();
          for (const code in schedulingStatusesObj) {
            const status = schedulingStatusesObj[code];
            statusIdToObjMap.set(status.id, status);
          }

          const leaveTypeLegendMap = new Map<string, string>();
          const leaveTypeNameToCodeMap = new Map<string, string>();

          const generateLeaveTypeCode = (name: string): string => {
            if (!name) return "";
            const existing = leaveTypeNameToCodeMap.get(name);
            if (existing) return existing;

            const usedCodes = new Set([
              ...leaveTypeLegendMap.keys(),
              ...leaveTypeNameToCodeMap.values(),
            ]);

            const words = name.trim().split(/\s+/);

            // Try initials first (e.g. "Sick Leave" -> "SL")
            if (words.length > 1) {
              const initials = words.map(w => w[0].toUpperCase()).join("");
              if (!usedCodes.has(initials)) {
                leaveTypeNameToCodeMap.set(name, initials);
                return initials;
              }
            }

            // Single word or initials collided: try first 1, 2, 3 chars uppercased
            const upper = name.replace(/\s+/g, "").toUpperCase();
            for (let len = words.length > 1 ? words.length : 1; len <= Math.min(upper.length, 3); len++) {
              const candidate = upper.substring(0, len);
              if (!usedCodes.has(candidate)) {
                leaveTypeNameToCodeMap.set(name, candidate);
                return candidate;
              }
            }

            // Fallback: try increasing length up to full name
            for (let len = 4; len <= upper.length; len++) {
              const candidate = upper.substring(0, len);
              if (!usedCodes.has(candidate)) {
                leaveTypeNameToCodeMap.set(name, candidate);
                return candidate;
              }
            }

            // Last resort: append number
            let i = 1;
            while (usedCodes.has(`${upper.substring(0, 3)}${i}`)) i++;
            const fallback = `${upper.substring(0, 3)}${i}`;
            leaveTypeNameToCodeMap.set(name, fallback);
            return fallback;
          };
          const requestTypeLegendMap = new Map<string, string>();
          const legendCellsToUpdate: { cell: any; legendStyle: any; }[] = [];

          const formatTimeDiffMinutes = (minutes: number): string => {
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            if (h > 0 && m > 0) return `${h}h ${m}m`;
            if (h > 0) return `${h}h`;
            return `${m}m`;
          };

          const getRequestTypeLabel = (shift: any): string => {
            if (!shift.requestTypeCode) return "";
            const code = shift.requestTypeCode.toLowerCase();
            if (code === "al") {
              requestTypeLegendMap.set("AL", "Arrive Late");
              const timePart = shift.timeDiffMinutes ? ` ${formatTimeDiffMinutes(shift.timeDiffMinutes)}` : "";
              return `AL${timePart}`;
            }
            if (code === "le") {
              requestTypeLegendMap.set("LE", "Leave Early");
              const timePart = shift.timeDiffMinutes ? ` ${formatTimeDiffMinutes(shift.timeDiffMinutes)}` : "";
              return `LE${timePart}`;
            }
            return "";
          };

          const getShiftStatusCellText = (shifts: any[], worker: any): string => {
            if (!shifts || shifts.length === 0) return "";
            return shifts.map((shift) => {
              const status = statusIdToObjMap.get(shift.schedulingStatusId);
              if (!status) return "";
              let statusCode = status.code;
              if (statusCode === "RAS" && (worker.isWorkcenterChanged || worker.isPatternChanged || worker.isJobChanged)) {
                statusCode = "SCH";
              }
              if (statusCode === "COF" && shift.leaveTypes && shift.leaveTypes.length > 0) {
                const leaveCodes = shift.leaveTypes.map((lt: any) => {
                  const code = generateLeaveTypeCode(lt.name);
                  leaveTypeLegendMap.set(code, lt.name);
                  return code;
                });
                const reqLabel = getRequestTypeLabel(shift);
                const displayStatusCode = statusCode === "COF" ? "TO" : statusCode;
                const parts = [displayStatusCode, ...leaveCodes];
                if (reqLabel) parts.push(reqLabel);
                return parts.join("\n");
              }
              const reqLabel = getRequestTypeLabel(shift);
              const displayStatusCode = statusCode === "COF" ? "TO" : statusCode;
              if (reqLabel) return `${displayStatusCode}\n${reqLabel}`;
              return displayStatusCode;
            }).join("\n");
          };

          const getConsolidatedStatusText = (worker: any, activeDates: Date[]): string => {
            const allWorkerShifts = worker.shifts || [];
            if (allWorkerShifts.length === 0) return "";

            const workerShifts = allWorkerShifts.filter((s: any) => {
              const shiftDate = startOfDay(new Date(s.startDateTime));
              return shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
            });

            if (workerShifts.length === 0) return "";

            const statusEntries: { date: number, code: string }[] = [];

            activeDates.forEach(date => {
              const shifts = workerShifts.filter((s: any) => isSameDay(new Date(s.startDateTime), date));
              if (shifts.length > 0) {
                const shift = shifts[0];
                const status = statusIdToObjMap.get(shift.schedulingStatusId);
                let code = status?.code || "SCH";
                if (code === "RAS" && (worker.isWorkcenterChanged || worker.isPatternChanged || worker.isJobChanged)) {
                  code = "SCH";
                }
                if (code === "COF" && shift.leaveTypes && shift.leaveTypes.length > 0) {
                  const leaveCodes = shift.leaveTypes.map((lt: any) => {
                    const leaveCode = generateLeaveTypeCode(lt.name);
                    leaveTypeLegendMap.set(leaveCode, lt.name);
                    return leaveCode;
                  });
                  const reqLabel = getRequestTypeLabel(shift);
                  const innerParts = [...leaveCodes];
                  if (reqLabel) innerParts.push(reqLabel);
                  code = `${code === "COF" ? "TO" : code}(${innerParts.join(" ")})`;
                } else {
                  const reqLabel = getRequestTypeLabel(shift);
                  if (reqLabel) code = `${code === "COF" ? "TO" : code}(${reqLabel})`;
                  else if (code === "COF") code = "TO";
                }
                statusEntries.push({ date: date.getDate(), code });
              }
            });

            if (statusEntries.length === 0) return "";

            // If all active days have the same status, just return the status code
            const allSame = statusEntries.every(e => e.code === statusEntries[0].code);
            if (allSame) return statusEntries[0].code;

            // Otherwise, group by status and list dates
            const groups: { code: string, dates: number[] }[] = [];
            statusEntries.forEach(entry => {
              const lastGroup = groups[groups.length - 1];
              if (lastGroup && lastGroup.code === entry.code) {
                lastGroup.dates.push(entry.date);
              } else {
                groups.push({ code: entry.code, dates: [entry.date] });
              }
            });

            return groups.map(g => `${g.code}-${g.dates.join(",")}`).join(", ");
          };

          const getStatusText = (worker: any): string => {
            const allShifts = worker.shifts || [];
            if (allShifts.length === 0) return "";
            
            const shifts = allShifts.filter((s: any) => {
              const shiftDate = startOfDay(new Date(s.startDateTime));
              return shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
            });

            if (shifts.length === 0) return "";
            
            // Optimization for all SCH or RAS status
            const allScheduledOrReassigned = shifts.every((s: any) => {
              const status = statusIdToObjMap.get(s.schedulingStatusId);
              return status?.code === "SCH" || status?.code === "RAS";
            });
            if (allScheduledOrReassigned) return "";

            // Build per-date status entries: "2-S,P", "3-In Training"
            const dateEntries: { dateNum: number; label: string }[] = [];

            shifts.forEach((s: any) => {
              const status = statusIdToObjMap.get(s.schedulingStatusId);
              if (!status) return;
              const code = status.code;
              const dateNum = new Date(s.startDateTime).getDate();

              if (code === "COF" && s.leaveTypes && s.leaveTypes.length > 0) {
                const leaveCodes = s.leaveTypes.map((lt: any) => {
                  const leaveCode = generateLeaveTypeCode(lt.name);
                  leaveTypeLegendMap.set(leaveCode, lt.name);
                  return leaveCode;
                });
                const reqLabel = getRequestTypeLabel(s);
                const labelParts = [...leaveCodes];
                if (reqLabel) labelParts.push(reqLabel);
                dateEntries.push({ dateNum, label: labelParts.join(" ") });
              } else if (s.requestTypeCode && (s.requestTypeCode.toLowerCase() === "al" || s.requestTypeCode.toLowerCase() === "le")) {
                const reqLabel = getRequestTypeLabel(s);
                dateEntries.push({ dateNum, label: reqLabel });
              } else if (code === "TRA") {
                dateEntries.push({ dateNum, label: "In Training" });
              } else if (code === "COF") {
                dateEntries.push({ dateNum, label: "TO" });
              } else if (code === "RDU") {
                dateEntries.push({ dateNum, label: "RDU" });
              }
            });

            if (dateEntries.length === 0) return "";

            // Group consecutive entries with the same label
            const groups: { label: string; dateNums: number[] }[] = [];
            dateEntries.sort((a, b) => a.dateNum - b.dateNum);
            dateEntries.forEach(entry => {
              const lastGroup = groups[groups.length - 1];
              if (lastGroup && lastGroup.label === entry.label) {
                if (!lastGroup.dateNums.includes(entry.dateNum)) lastGroup.dateNums.push(entry.dateNum);
              } else {
                groups.push({ label: entry.label, dateNums: [entry.dateNum] });
              }
            });

            return groups.map(g => `${g.dateNums.join(",")}-${g.label}`).join("\n");
          };

          const groupWorkersByJob = (workers: any[]) => {
            const groupedWorkers = new Map<number | null, any[]>();
            workers.forEach((worker) => {
              const relevantShifts = worker.shifts || [];
              const isDefaultEntry = !worker.isWorkcenterChanged && !worker.isJobChanged && !worker.isPatternChanged;
              let targetJobId: number | null;
              if (isDefaultEntry) {
                targetJobId = worker.primaryJobId || null;
              } else {
                targetJobId = relevantShifts.length > 0 ? relevantShifts[0].uPrimaryJobId || null : worker.primaryJobId || null;
              }
              if (!groupedWorkers.has(targetJobId)) groupedWorkers.set(targetJobId, []);
              groupedWorkers.get(targetJobId)!.push(worker);
            });
            return groupedWorkers;
          };

          const getShiftSummaryForPattern = (workers: any[]) => {
            const shiftsByDate: Record<string, Map<string, { start: string; end: string }>> = {};
            for (const day of dateColumns) shiftsByDate[format(day, "yyyy-MM-dd")] = new Map();
            
            for (const worker of workers) {
              const workerShifts = (worker.shifts || []).filter((s: any) => {
                const shiftDate = startOfDay(new Date(s.startDateTime));
                return shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
              });

              for (const shift of workerShifts) {
                if (!shift.isFlexShift) {
                  const dateKey = format(new Date(shift.startDateTime), "yyyy-MM-dd");
                  if (shiftsByDate[dateKey]) {
                    const timeKey = `${shift.startDateTime}_${shift.endDateTime}`;
                    if (!shiftsByDate[dateKey].has(timeKey)) {
                      shiftsByDate[dateKey].set(timeKey, { start: shift.startDateTime, end: shift.endDateTime });
                    }
                  }
                }
              }
            }
            const summary: Record<string, string> = {};
            for (const dateKey in shiftsByDate) {
              const shifts = Array.from(shiftsByDate[dateKey].values());
              summary[dateKey] = shifts.map((s) => format(new Date(s.start), "H:mm") + "-" + format(new Date(s.end), "H:mm")).join("\n");
            }
            return summary;
          };

          summarySheet.pageSetup = {
            orientation: "landscape",
            fitToPage: true,
            fitToHeight: 0,
            fitToWidth: 1,
            margins: { left: 0.2, right: 0.2, top: 0.2, bottom: 0.2, header: 0.1, footer: 0.1 }
          };

          summarySheet.columns = [
            { key: "name", width: 40 },
            { key: "empty1", width: 0 }, { key: "empty2", width: 0 },
            ...dateColumns.map((date, i) => ({ key: `day${i}`, width: 22 })),
          ];

          let summaryCurrentRow = 1;
          const spSummaryHeaderRow = summarySheet.getRow(summaryCurrentRow++);
          spSummaryHeaderRow.getCell("A").value = t(
            "full-time-worker-scheduler_shift-pattern-summary"
          );
          summarySheet.mergeCells(spSummaryHeaderRow.number, 1, spSummaryHeaderRow.number, 3);
          spSummaryHeaderRow.eachCell(c => c.style = mainHeaderStyle);
          dateColumns.forEach((day, i) => {
            const cell = spSummaryHeaderRow.getCell(i + 4);
            cell.value = format(day, "EEE d", { locale: dateLocale });
            cell.style = dateHeaderStyle;
          });
          spSummaryHeaderRow.height = 25;

          const allShiftPatternsMap = new Map<number, { id: number; workers: any[] }>();
          const unassignedByWc = new Map<number, any[]>();
          const flexByWc = new Map<number, any[]>();

          workerWorkcenterShiftPatternsMap.forEach(wc => {
            wc.shiftPatterns.forEach((sp: any) => {
              if (!allShiftPatternsMap.has(sp.id)) allShiftPatternsMap.set(sp.id, { id: sp.id, workers: [] });
              allShiftPatternsMap.get(sp.id)!.workers.push(...sp.workers);
            });
            if (wc.unassignedWorkers?.length > 0) unassignedByWc.set(wc.id, wc.unassignedWorkers);
            if (wc.flexWorkers?.length > 0) flexByWc.set(wc.id, wc.flexWorkers);
          });

          for (const [spId, spData] of allShiftPatternsMap.entries()) {
            const summaryData = getShiftSummaryForPattern(spData.workers);
            const spName = shiftPatternsObj[spId]?.name ||
              t("full-time-worker-scheduler_unknown-pattern");
            const row = summarySheet.getRow(summaryCurrentRow++);
            row.values = [spName, null, null, ...dateColumns.map(day => summaryData[format(day, "yyyy-MM-dd")] || "")];
            summarySheet.mergeCells(row.number, 1, row.number, 3);
            row.getCell("A").style = shiftSummaryNameStyle;
            for (let i = 4; i <= 10; i++) row.getCell(i).style = shiftSummaryTimeStyle;
            row.height = 25;
          }

          // --- 5. Build Shift Pattern Specific Sheets ---
          const processPatternSheet = (spId: number | string, workers: any[], sheetName: string, isSpecial = false) => {
            const worksheet = workbook.addWorksheet(sheetName.substring(0, 31).replace(/[\\\/\?\*\[\]]/g, "_"));

            // Set page setup to landscape and fit to page width
            worksheet.pageSetup = {
              orientation: "landscape",
              fitToPage: true,
              fitToHeight: 0, // infinite height
              fitToWidth: 1,  // fit to 1 page width
              margins: {
                left: 0.2, right: 0.2,
                top: 0.2, bottom: 0.2,
                header: 0.1, footer: 0.1
              }
            };

            const summaryData = getShiftSummaryForPattern(workers);
            const activeDates = dateColumns.filter(day => {
              if (summaryData[format(day, "yyyy-MM-dd")] !== "") return true;
              // Also check if any worker has a shift on this date (e.g., COF/TRA shifts without time entries)
              return workers.some(w => (w.shifts || []).some((s: any) => {
                const shiftDate = startOfDay(new Date(s.startDateTime));
                return isSameDay(shiftDate, day) && shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
              }));
            });
            const isConsolidated = activeDates.length > 0 && activeDates.length < 7 && !isSpecial;
            const displayDates = activeDates;
            const totalColumns = 6 + displayDates.length;

            if (isConsolidated) {
              worksheet.columns = [
                { key: "sNo", width: 8 },
                { key: "badge", width: 18 },
                { key: "name", width: 25 },
                { key: "workerType", width: 15 },
                { key: "status", width: 12 },
                { key: "response", width: 15 },
                { key: "schedule", width: 40 },
              ];
            } else {
              worksheet.columns = [
                { key: "sNo", width: 8 },
                { key: "badge", width: 18 },
                { key: "name", width: 25 },
                { key: "workerType", width: 15 },
                { key: "status", width: 12 },
                { key: "response", width: 15 },
                ...displayDates.map((date, i) => ({ key: `day${i}`, width: 15 })),
              ];
            }

            let currentRow = 1;

            workerWorkcenterShiftPatternsMap.forEach(wc => {
              let workersInWcForSp: any[] = [];
              if (!isSpecial) {
                const sp = wc.shiftPatterns.find((p: any) => p.id === spId);
                if (sp) workersInWcForSp = sp.workers;
              } else {
                if (spId === "unassigned") workersInWcForSp = wc.unassignedWorkers;
                else if (spId === "flex") workersInWcForSp = wc.flexWorkers;
              }
              if (workersInWcForSp.length === 0) return;

              // --- New Rows before each Work Center ---
              // Row 1: Crew Name (Shift Pattern Name)
              const crewNameRow = worksheet.getRow(currentRow++);
              crewNameRow.getCell("A").value = sheetName;
              worksheet.mergeCells(crewNameRow.number, 1, crewNameRow.number, 6);
              crewNameRow.eachCell(c => c.style = mainHeaderStyle);

              if (isConsolidated) {
                const cell = crewNameRow.getCell(7);
                const daysText = activeDates.map(d => format(d, "d - EEE", { locale: dateLocale })).join(", ");
                const firstDate = activeDates[0];
                const timeRange = summaryData[format(firstDate, "yyyy-MM-dd")];
                cell.value = {
                  richText: [
                    { font: { bold: true, name: "Roboto", size: 12 }, text: daysText + "\n" },
                    { font: { bold: false, name: "Roboto", size: 10 }, text: timeRange }
                  ]
                };
                cell.style = { ...dateHeaderStyle, alignment: { ...dateHeaderStyle.alignment, wrapText: true, vertical: "middle", horizontal: "center" } };
              } else {
                displayDates.forEach((day, i) => {
                  const cell = crewNameRow.getCell(i + 7);
                  cell.value = format(day, "d - EEE", { locale: dateLocale });
                  cell.style = dateHeaderStyle;
                });
              }
              crewNameRow.height = 40;

              // Row 2: Legends
              const legendRowWC = worksheet.getRow(currentRow++);
              const legendItemsWC = Object.values(schedulingStatusesObj)
                .filter((status: any) => status.code !== "RAS")
                .map((status: any) => `${status.code === "COF" ? "TO" : status.code}: ${status.name === "Called Off" ? t("full-time-worker-scheduler_time-off") : status.name}`);

              const cell = legendRowWC.getCell(1);
              cell.value = legendItemsWC.join(", ");
              cell.style = {
                ...legendCellStyle,
                alignment: { vertical: "middle", horizontal: "left" }
              };
              legendCellsToUpdate.push({ cell, legendStyle: { ...legendCellStyle, alignment: { vertical: "middle", horizontal: "left" } } });
              worksheet.mergeCells(legendRowWC.number, 1, legendRowWC.number, isConsolidated ? 7 : totalColumns);
              legendRowWC.height = 20;

              const wcRow = worksheet.getRow(currentRow++);
              wcRow.getCell("A").value = `${wc.name}`;
              worksheet.mergeCells(wcRow.number, 1, wcRow.number, isConsolidated ? 7 : totalColumns);
              wcRow.eachCell((cell) => (cell.style = wcHeaderStyle));
              wcRow.height = 30;

              const workerHeaderRow = worksheet.getRow(currentRow++);
              workerHeaderRow.values = [
                t("full-time-worker-scheduler_serial-no"),
                t("full-time-worker-scheduler_badge"),
                t("full-time-worker-scheduler_employee-name"),
                t("full-time-worker-scheduler_worker-type"),
                t("full-time-worker-scheduler_status"),
                t("full-time-worker-scheduler_response"),
                ...(isConsolidated
                  ? [t("full-time-worker-scheduler_schedule")]
                  : []),
              ];
              for (let i = 1; i <= 6; i++) workerHeaderRow.getCell(i).style = mainHeaderStyle;
              if (isConsolidated) {
                workerHeaderRow.getCell(7).style = mainHeaderStyle;
              } else {
                for (let i = 7; i <= totalColumns; i++) workerHeaderRow.getCell(i).style = { border };
              }
              workerHeaderRow.height = 25;

              const groupedByJob = groupWorkersByJob(workersInWcForSp);
              const sortedJobIds = Array.from(groupedByJob.keys()).sort((a, b) => {
                const nameA = allJobsObj[a!]?.name || "Unassigned";
                const nameB = allJobsObj[b!]?.name || "Unassigned";
                return nameA.localeCompare(nameB);
              });

              for (const jobId of sortedJobIds) {
                let serialNo = 1;
                const workersInJob = groupedByJob.get(jobId)!;
                const jobName = allJobsObj[jobId!]?.name || "Unassigned";

                // Calculate Requirements and Confirmed across all shifts in the week
                let maxRequiredCount = 0;
                let maxConfirmedCount = 0;
                const dailyOpenPositions: Record<string, number> = {};

                activeDates.forEach(date => {
                  const dateKey = format(date, "yyyy-MM-dd");
                  let dailyConfirmed = 0;
                  const workersScheduledOnDay = workersInJob.filter(w => 
                    (w.shifts || []).some((s: any) => {
                      if (!isSameDay(new Date(s.startDateTime), date)) return false;
                      const shiftDate = startOfDay(new Date(s.startDateTime));
                      if (shiftDate < startOfWeekDate || shiftDate >= endOfWeekDate) return false;
                      const status = statusIdToObjMap.get(s.schedulingStatusId);
                      return status?.code === "SCH" || status?.code === "RAS";
                    })
                  );
                  dailyConfirmed = workersScheduledOnDay.length;
                  
                  let dailyRequired = dailyConfirmed;
                  if (fetchFTWorkerShiftDetailsData) {
                    const shiftIds = new Set(workersInJob.flatMap(w => 
                      (w.shifts || []).filter((s: any) => {
                        if (s.isFlexShift || !isSameDay(new Date(s.startDateTime), date)) return false;
                        const shiftDate = startOfDay(new Date(s.startDateTime));
                        return shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
                      }).map((s: any) => s.shiftId)
                    ));

                    if (shiftIds.size > 0) {
                      const firstShiftId = Array.from(shiftIds)[0];
                      const details = fetchFTWorkerShiftDetailsData.find((d: any) => d.shiftId === firstShiftId && d.workcenterId === wc.id);
                      if (details) {
                        const jobDetail = details.jobs?.find((j: any) => j.jobId === jobId);
                        if (jobDetail) {
                          if (jobDetail.status === "US") dailyRequired = dailyConfirmed + jobDetail.value;
                          else if (jobDetail.status === "OS") dailyRequired = dailyConfirmed - jobDetail.value;
                        }
                      }
                    }
                  }

                  if (dailyRequired > dailyConfirmed) {
                    dailyOpenPositions[dateKey] = dailyRequired - dailyConfirmed;
                  }
                  maxRequiredCount = Math.max(maxRequiredCount, dailyRequired);
                  maxConfirmedCount = Math.max(maxConfirmedCount, dailyConfirmed);
                });

                const jobRow = worksheet.getRow(currentRow++);
                jobRow.getCell("A").value = t(
                  "full-time-worker-scheduler_job-summary",
                  {
                    jobName,
                    confirmed: maxConfirmedCount,
                    required: maxRequiredCount,
                  }
                );
                jobRow.getCell("A").style = jobHeaderStyle;
                worksheet.mergeCells(jobRow.number, 1, jobRow.number, isConsolidated ? 7 : totalColumns);
                jobRow.height = 20;

                const sortedWorkers = workersInJob.sort((a: any, b: any) => {
                  const badgeA = a.badgeNumber || a.companyEmployeeId;
                  const badgeB = b.badgeNumber || b.companyEmployeeId;
                  
                  if (badgeA === undefined || badgeA === null || badgeA === "") return 1;
                  if (badgeB === undefined || badgeB === null || badgeB === "") return -1;
                  
                  // Ensure numeric comparison if both are numbers or numeric strings
                  const numA = Number(badgeA);
                  const numB = Number(badgeB);
                  
                  if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB;
                  }
                  
                  return String(badgeA).trim().localeCompare(String(badgeB).trim(), undefined, { numeric: true, sensitivity: "base" });
                });

                for (const worker of sortedWorkers) {
                  const workerType = filterData.workerTypesObj[worker.workerTypeId]?.name === "Part Time" ? "Temp" : filterData.workerTypesObj[worker.workerTypeId]?.name || "";
                  const badge = worker.badgeNumber || worker.companyEmployeeId || "";
                  
                  const row = worksheet.getRow(currentRow++);
                  
                  if (isConsolidated) {
                    const consolidatedStatus = getConsolidatedStatusText(worker, activeDates);
                    row.values = [serialNo++, badge, `${worker.firstName} ${worker.lastName}`, workerType, getStatusText(worker), "", consolidatedStatus];
                    row.height = 25;
                    row.eachCell((cell, colNum) => {
                      cell.style = workerCellStyle;
                      if (colNum === 7) {
                        cell.style = { ...workerCellStyle, alignment: { ...workerCellStyle.alignment, horizontal: "center" } };
                      }
                    });
                  } else {
                    const allShifts = worker.shifts || [];
                    const shiftsByDay = displayDates.map(day => allShifts.filter((s: any) => {
                      const isSame = isSameDay(new Date(s.startDateTime), day);
                      if (!isSame) return false;
                      const shiftDate = startOfDay(new Date(s.startDateTime));
                      return shiftDate >= startOfWeekDate && shiftDate < endOfWeekDate;
                    }));
                    row.values = [serialNo++, badge, `${worker.firstName} ${worker.lastName}`, workerType, getStatusText(worker), "", ...shiftsByDay.map(shifts => getShiftStatusCellText(shifts, worker))];
                    row.height = 25;
                    row.eachCell((cell, colNum) => {
                      if (colNum <= 6) cell.style = workerCellStyle;
                      else {
                        const shifts = shiftsByDay[colNum - 7];
                        const shift = shifts.length > 0 ? shifts[0] : null;
                        let statusColor = "#FFFFFF";
                        if (shift) {
                          const s = statusIdToObjMap.get(shift.schedulingStatusId);
                          if (s) {
                            let code = s.code;
                            if (code === "RAS" && (worker.isWorkcenterChanged || worker.isPatternChanged || worker.isJobChanged)) code = "SCH";
                            statusColor = schedulingStatusesObj[code]?.backgroundColor || "#FFFFFF";
                          }
                        }
                        cell.style = { ...workerStatusCellStyle, fill: { type: "pattern", pattern: "solid", fgColor: { argb: statusColor.replace("#", "FF") } }, font: { ...workerStatusCellStyle.font, color: { argb: "FF000000" } } };
                      }
                    });
                  }
                }

                // Add Open Positions
                const allOpenDates = Object.keys(dailyOpenPositions);
                if (allOpenDates.length > 0) {
                  const isAllDaysOpen = activeDates.every(d => dailyOpenPositions[format(d, "yyyy-MM-dd")] > 0);
                  const maxDeficit = Math.max(...Object.values(dailyOpenPositions));
                  
                  for (let i = 0; i < maxDeficit; i++) {
                    const row = worksheet.getRow(currentRow++);
                    let openText = t("full-time-worker-scheduler_open");
                    if (!isAllDaysOpen) {
                      const datesForThisIndex = activeDates
                        .filter(d => (dailyOpenPositions[format(d, "yyyy-MM-dd")] || 0) > i)
                        .map(d => format(d, "d EEE", { locale: dateLocale }))
                        .join(", ");
                      openText = t("full-time-worker-scheduler_open-dates", {
                        dates: datesForThisIndex,
                      });
                    }
                    
                    row.values = ["", "", openText, "", "", "", ...(isConsolidated ? [""] : displayDates.map(() => ""))];
                    row.height = 25;
                    row.eachCell((cell, colNum) => {
                      cell.style = openWorkerCellStyle;
                      if (colNum > 6) cell.style = { ...openWorkerCellStyle, border };
                    });
                  }
                }
              }
              worksheet.getRow(currentRow - 1).addPageBreak();
              currentRow++;
            });
          };

          for (const [spId, spData] of allShiftPatternsMap.entries()) {
            processPatternSheet(spId, spData.workers, shiftPatternsObj[spId]?.name || `Pattern ${spId}`);
          }
          // Unknown Shifts tab removed per requirement
          if (Array.from(flexByWc.values()).flat().length > 0) processPatternSheet("flex", Array.from(flexByWc.values()).flat(), t("full-time-worker-scheduler_flex-workers"), true);

          // Update legend cells with leave type codes after all workers have been processed
          const extraLegendItems: string[] = [];
          if (leaveTypeLegendMap.size > 0) {
            extraLegendItems.push(...Array.from(leaveTypeLegendMap.entries()).map(([code, name]) => `${code}: ${name}`));
          }
          if (requestTypeLegendMap.size > 0) {
            extraLegendItems.push(...Array.from(requestTypeLegendMap.entries()).map(([code, name]) => `${code}: ${name}`));
          }
          if (extraLegendItems.length > 0) {
            for (const { cell: legendCell, legendStyle } of legendCellsToUpdate) {
              const existingValue = legendCell.value as string;
              legendCell.value = existingValue + ", " + extraLegendItems.join(", ");
              legendCell.style = legendStyle;
            }
          }

          // --- 6. Build QC - Supervisors Sheet ---
          const qcSupervisorsSheet = workbook.addWorksheet(
            t("full-time-worker-scheduler_qc-supervisors-tab")
          );
          qcSupervisorsSheet.pageSetup = {
            paperSize: 1, // US Letter
            orientation: "landscape",
            fitToPage: false,
            fitToWidth: 1,
            margins: { left: 0.2, right: 0.2, top: 0.2, bottom: 0.2, header: 0.1, footer: 0.1 }
          };

          qcSupervisorsSheet.columns = [
            { header: t("full-time-worker-scheduler_qc-supervisors"), key: "rowLabel", width: 45 },
            { header: "", key: "col1", width: 20 },
            { header: "", key: "col2", width: 20 },
            { header: "", key: "col3", width: 20 },
            { header: "", key: "col4", width: 20 },
            { header: "", key: "col5", width: 20 },
            { header: "", key: "col6", width: 20 },
          ];

          let qcCurrentRow = 1;
          const qcTitleRow = qcSupervisorsSheet.getRow(qcCurrentRow++);
          qcTitleRow.getCell("A").value = t(
            "full-time-worker-scheduler_qc-supervisors"
          );
          qcSupervisorsSheet.mergeCells(qcTitleRow.number, 1, qcTitleRow.number, 7);
          qcTitleRow.eachCell(c => {
            c.style = {
              ...mainHeaderStyle,
              fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } }
            };
          });
          qcTitleRow.height = 30;

          const spToWcs = new Map<number, Set<string>>();
          workerWorkcenterShiftPatternsMap.forEach(wc => {
            wc.shiftPatterns.forEach((sp: any) => {
              if (!spToWcs.has(sp.id)) spToWcs.set(sp.id, new Set());
              spToWcs.get(sp.id)!.add(wc.name);
            });
          });

          const sortedSpIds = Array.from(spToWcs.keys()).sort((a, b) => {
            const nameA = shiftPatternsObj[a]?.name || "";
            const nameB = shiftPatternsObj[b]?.name || "";
            return nameA.localeCompare(nameB);
          });

          sortedSpIds.forEach((spId, index) => {
            const spName = shiftPatternsObj[spId]?.name || `Pattern ${spId}`;
            const wcs = Array.from(spToWcs.get(spId)!).sort();

            // Add Header row for supervisor entries
            const headerRow = qcSupervisorsSheet.getRow(qcCurrentRow++);
            headerRow.values = ["", "", "", "", "", "", ""];
            headerRow.height = 25;
            headerRow.eachCell(c => {
              c.style = {
                ...dateHeaderStyle,
                font: { ...dateHeaderStyle.font, size: 10 },
                alignment: { vertical: "middle", horizontal: "center" }
              };
            });
            
            wcs.forEach(wcName => {
              const row = qcSupervisorsSheet.getRow(qcCurrentRow++);
              row.values = [`${spName} - ${wcName}`, "", "", "", "", "", ""];
              row.height = 30;
              row.eachCell(c => {
                c.style = workerCellStyle;
              });
            });

            if (index < sortedSpIds.length - 1) {
              qcSupervisorsSheet.getRow(qcCurrentRow - 1).addPageBreak();
            }
          });

          // --- 7. Build Head Count Sheet ---
          const headCountSheet = workbook.addWorksheet(
            t("full-time-worker-scheduler_head-count")
          );
          headCountSheet.pageSetup = {
            orientation: "landscape",
            fitToPage: true,
            fitToHeight: 0,
            fitToWidth: 1,
            margins: { left: 0.2, right: 0.2, top: 0.2, bottom: 0.2, header: 0.1, footer: 0.1 }
          };
          headCountSheet.columns = [
            { header: t("full-time-worker-scheduler_shift-pattern-crew"), key: "sp", width: 30 },
            { header: t("full-time-worker-scheduler_work-center"), key: "wc", width: 30 },
            { header: t("full-time-worker-scheduler_total-workers"), key: "total", width: 15 },
            { header: "TRA", key: "tra", width: 10 },
            { header: "TO", key: "cof", width: 10 },
            { header: "RDU", key: "rdu", width: 10 },
            { header: t("full-time-worker-scheduler_active"), key: "active", width: 15 },
          ];
          // Set headers manually since auto-headers don't always apply styles or appear correctly with mergeCells/other operations
          const headCountHeaderRow = headCountSheet.getRow(1);
          headCountHeaderRow.values = headCountSheet.columns.map(c => c.header as string);
          headCountHeaderRow.eachCell(c => c.style = mainHeaderStyle);

          workerWorkcenterShiftPatternsMap.forEach(wc => {
            wc.shiftPatterns.forEach((sp: any) => {
              const spName =
                shiftPatternsObj[sp.id]?.name ||
                t("full-time-worker-scheduler_unknown-pattern");
              const workers = sp.workers || [];
              const tra = workers.filter(
                (w: any) => getStatusText(w) === "TRA"
              ).length;
              const cof = workers.filter(
                (w: any) => getStatusText(w) === "TO"
              ).length;
              const rdu = workers.filter(
                (w: any) => getStatusText(w) === "RDU"
              ).length;
              const total = workers.length;
              const active = total - (tra + cof + rdu);
              const row = headCountSheet.addRow([spName, wc.name, total, tra, cof, rdu, active]);
              row.eachCell(c => c.style = workerCellStyle);
            });
          });

          const buffer = await workbook.xlsx.writeBuffer();
          const fileName = `Schedule_${format(startOfWeekDate, "dd-M-yyyy")}_${format(addDays(startOfWeekDate, 6), "dd-M-yyyy")}_${selectedLocationName}.xlsx`;
          saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
        } catch (error) {
          console.error("Error during Excel export:", error);
        } finally {
          setIsExporting(false);
          setIsFetchingDetails(false);
        }
      };
      if (isFetchingDetails) {
        if (fetchFTWorkerShiftDetailsStatus === "fulfilled" || fetchFTWorkerShiftDetailsStatus === "error") {
          exportToExcel();
        }
      } else {
        exportToExcel();
      }
    }
  }, [isExporting, workerWorkcenterShiftPatternsMap, fetchFTWorkerFiltersStatus, fetchFTWorkerShiftsStatus, startingDay, selectedLocationName, shiftPatternsObj, allJobsObj, schedulingStatusesObj, allWorkcentersObj, fetchFTWorkerShiftDetailsData, isFetchingDetails, fetchFTWorkerShiftDetailsStatus, filterData.workerTypesObj, t, dateLocale]);

	const handleWorkcenterToggle = useCallback(
		(workcenterId: number) => {
			isWorkcenterOpen?.(workcenterId)
				? closeWorkcenter?.(workcenterId)
				: openWorkcenter?.(workcenterId);
		},
		[isWorkcenterOpen, closeWorkcenter, openWorkcenter]
	);

	const handleShiftPatternToggle = useCallback(
		(shiftPattern: string) => {
			isShiftPatternOpen?.(shiftPattern)
				? closeShiftPattern?.(shiftPattern)
				: openShiftPattern?.(shiftPattern);
		},
		[isShiftPatternOpen, closeShiftPattern, openShiftPattern]
	);

	useEffect(() => {
		// This function calculate X and Y
		const getPosition = () => {
			if (boxRef) {
				try {
					const y = boxRef.current.offsetTop;
					setSchedulerContainerHeight(height - y);
				} catch (error) {
					// Do nothing
				}
			}
		};
		getPosition();
	}, [height]);

	return (
    <Box width="100%">
      <Divider />
      <Box>
        <FTCalendarToolbar
          isFilterOpen={isPrinting ? false : isFilterOpen}
          currentTab={WORKERS}
          setFilterOpen={() => setIsFilterOpen(!isFilterOpen)}
          handleForward={() => dispatch(fetchFTWorkerShifts("NEXT_WEEK"))}
          handleBack={() => dispatch(fetchFTWorkerShifts("PREVIOUS_WEEK"))}
          handleToday={() => dispatch(fetchFTWorkerShifts("TODAY"))}
          handleCalendarClick={(date) => {
            dispatch(fetchFTWorkerShifts(date));
          }}
          startingDay={startingDay}
          isLoading={
            fetchFTWorkerFiltersStatus === "pending" ||
            fetchFTWorkerShiftsStatus === "pending" ||
            fetchFTWorkerShiftsStatus === "idle"
          }
          setIsPrinting={setIsPrinting}
          handleExport={handleExport}
          isExporting={isExporting}
        />
      </Box>

      <Box ref={boxRef} sx={{ flexGrow: 1 }}>
        <Grid2
          container
          wrap="nowrap"
          spacing={0}
          sx={{ position: "relative" }}
        >
          <Box
            sx={{
              flexGrow: 1,
            }}
          >
            <Grid2
              sx={{
                transition: "padding-left 0.3s ease",
                height: `calc(${schedulerContainerHeight}px - 24px)`,
                overflow: isPrinting ? "visible" : "hidden auto",
              }}
            >
              <Divider />
              {!isPrinting && (
                <AppliedFilters />
              )}
              <Box
                sx={{
                  flexShrink: 0,
                }}
              >
                {!isPrinting &&
                  fetchFTWorkerFiltersStatus === "fulfilled" &&
                  fetchFTWorkerShiftsStatus === "fulfilled" && (
                    <CalendarHeader isPrinting={false} />
                  )}
                {!isPrinting && (
                  <WorkerSchedulerSkeleton
                    isWorkerFiltersStatus={
                      fetchFTWorkerFiltersStatus === "pending"
                    }
                    isWorkerShiftsStatus={
                      fetchFTWorkerShiftsStatus === "pending"
                    }
                  />
                )}
              </Box>
              {viewMode?.[0] === "WORKCENTER" &&
                fetchFTWorkerFiltersStatus === "fulfilled" &&
                fetchFTWorkerShiftsStatus === "fulfilled" && (
                  <WorkcenterShiftpatternView
                    workerWorkcenterShiftPatternsMap={
                      workerWorkcenterShiftPatternsMap
                    }
                    schedulerContainerHeight={schedulerContainerHeight}
                    selectedLocation={selectedLocation}
                    isWorkcenterOpen={(value: number) =>
                      searchedTerm !== "" || isPrinting
                        ? true
                        : isWorkcenterOpen(value)
                    }
                    handleWorkcenterToggle={handleWorkcenterToggle}
                    shiftPatternsObj={shiftPatternsObj}
                    isShiftPatternOpen={(value: string) =>
                      searchedTerm !== "" || isPrinting
                        ? true
                        : isShiftPatternOpen(value)
                    }
                    handleShiftPatternToggle={handleShiftPatternToggle}
                    isAppliedFilterInView={appliedFilters?.length > 0}
                    isPrinting={isPrinting}
                    startingDay={startingDay}
                  />
                )}
            </Grid2>
          </Box>
        </Grid2>
      </Box>

      {/* Global Dialog Manager - handles all status transition dialogs */}
      <GlobalTransitionDialogManager />
    </Box>
  );
};

WorkerScheduler.displayName = "WorkerScheduler";

export default React.memo(WorkerScheduler);
